import { Box } from "@mui/material";
import { useMemo } from "react";
import qrcode from "qrcode-generator";

/**
 * A QR code, drawn as an inline SVG.
 *
 * **Rendered entirely in the browser, on purpose.** The first thing anyone
 * reaches for is a QR *image service* (`api.qrserver.com`, Google Charts…) —
 * that would put the value in a third party's URL. The values we encode here
 * include the TOTP shared secret (`otpauth://…?secret=…`), which is a
 * credential: it must never leave the device. `qrcode-generator` is a
 * zero-dependency pure-JS encoder, so the whole thing stays local and works
 * offline (and inside the Tauri desktop shell, which has no proxy at the
 * `tauri://` origin).
 *
 * **Always black-on-white, in both themes.** Scanners need dark modules on a
 * light ground plus a light quiet zone; inverting it for dark mode is what
 * makes phone cameras silently fail to lock on. So the two colours are
 * hardcoded rather than pulled from the palette — this is the one place in
 * the app where a raw hex is the correct answer.
 */

/** Modules of light margin around the symbol. 4 is the spec minimum. */
const QUIET_ZONE = 4;

export type QrErrorCorrection = "L" | "M" | "Q" | "H";

/**
 * Encode `value` and return an SVG path covering every dark module, plus the
 * side length of the (quiet-zone-inclusive) symbol in module units.
 *
 * Returns `null` for an empty string or a value the encoder refuses (too long
 * for the largest symbol at this correction level) so callers can fall back to
 * showing the raw value instead of crashing the surface.
 */
export function qrSvgPath(
  value: string,
  level: QrErrorCorrection = "M",
): { d: string; size: number } | null {
  if (!value) return null;
  let count: number;
  let isDark: (row: number, col: number) => boolean;
  try {
    const qr = qrcode(0, level);
    qr.addData(value);
    qr.make();
    count = qr.getModuleCount();
    isDark = (row, col) => qr.isDark(row, col);
  } catch {
    return null;
  }

  // One 1×1 sub-path per dark module. `crispEdges` + integer coordinates keep
  // the module grid pixel-exact at any rendered size, which is what a scanner
  // needs; anti-aliased half-pixels are what makes small QR codes unreadable.
  let d = "";
  for (let row = 0; row < count; row++) {
    for (let col = 0; col < count; col++) {
      if (isDark(row, col)) d += `M${col + QUIET_ZONE} ${row + QUIET_ZONE}h1v1h-1z`;
    }
  }
  return { d, size: count + QUIET_ZONE * 2 };
}

export function QrCode({
  value,
  label,
  size = 168,
  level = "M",
}: {
  value: string;
  /** Accessible name — say what scanning it *does*, not "QR code". */
  label: string;
  /** Rendered side length in px (the symbol is square). */
  size?: number;
  level?: QrErrorCorrection;
}): JSX.Element | null {
  const symbol = useMemo(() => qrSvgPath(value, level), [value, level]);
  if (!symbol) return null;

  return (
    <Box
      component="svg"
      role="img"
      aria-label={label}
      viewBox={`0 0 ${symbol.size} ${symbol.size}`}
      shapeRendering="crispEdges"
      sx={{ display: "block", width: size, height: size }}
    >
      <rect width={symbol.size} height={symbol.size} fill="#ffffff" />
      <path d={symbol.d} fill="#000000" />
    </Box>
  );
}
